import dayjs, { Dayjs } from 'dayjs'
import { CrewMember } from '../scheduleApi'
import { EventFormData } from './eventFormTypes'
import { parseWorkareaScheduleForEvent } from './recurringScheduleModel'
import { expandRecurrenceDates } from '../CreateEvent/recurrenceExpander'
import { Duration } from '../../common/durationModel'

type RecurrenceFields = {
  startDate: Dayjs
  repeatUnit: string
  repeatCount: number
  repeatPlace: string[]
  repeatMonth: number[]
}

const buildRecurrenceConfig = (fields: RecurrenceFields) => ({
  startDate: fields.startDate,
  endDate: null as Dayjs | null,
  repeatUnits: fields.repeatUnit,
  repeatCount: fields.repeatCount,
  repeatingDayUnit: fields.startDate.format('dddd').toLowerCase(),
  repeatPlace: fields.repeatPlace ? fields.repeatPlace.join(',') : null,
  repeatMonth: fields.repeatMonth ? fields.repeatMonth.join(',') : null,
})

type OccurrencesInput = RecurrenceFields & {
  repeatEndType: string
  repeatOccurrences: number
}

export const calculateRepeatEndsOnFromOccurrences = (input: OccurrencesInput): Dayjs | null => {
  if (input.repeatEndType !== 'occurrences' || input.repeatUnit === 'none' || input.repeatOccurrences <= 0) {
    return null
  }

  const searchBound = input.startDate.add(
    input.repeatOccurrences * input.repeatCount,
    input.repeatUnit as dayjs.ManipulateType,
  )
  const dates = expandRecurrenceDates({
    ...buildRecurrenceConfig(input),
    endDate: searchBound,
  })

  if (dates.length >= input.repeatOccurrences) {
    return dates[input.repeatOccurrences - 1]
  }
  return dates[dates.length - 1] ?? input.startDate
}

type EndDateInput = RecurrenceFields & {
  repeatEndType: string
  repeatEndsOn: Dayjs | null
}

export const calculateOccurrencesFromEndDate = (input: EndDateInput): number | null => {
  if (input.repeatEndType !== 'date' || input.repeatUnit === 'none' || !input.repeatEndsOn) {
    return null
  }

  const dates = expandRecurrenceDates({
    ...buildRecurrenceConfig(input),
    endDate: input.repeatEndsOn,
  })

  return Math.max(dates.length, 1)
}

type ResolveEndDateInput = {
  endDateMode: EventFormData['endDateMode']
  startDate: Dayjs
  endDate: Dayjs
}

export const resolveEndDate = (input: ResolveEndDateInput): Dayjs => {
  if (input.endDateMode === 'sameDay') {
    return input.startDate
  }
  return input.endDate
}

type DeriveEndDateModeInput = {
  isAllDay: boolean
  startDate: Dayjs
  endDate: Dayjs
}

export const deriveEndDateMode = (input: DeriveEndDateModeInput): EventFormData['endDateMode'] => {
  if (input.isAllDay && input.endDate.isSame(input.startDate, 'day')) {
    return 'sameDay'
  }
  return 'manual'
}

export const defaultEventFormData = (): EventFormData => ({
  startDate: dayjs('2026-04-10'),
  startTime: dayjs('2026-04-10T08:00:00'),
  estimatedDuration: Duration.minutes(60),
  endDate: dayjs('2026-04-10'),
  isAllDay: false,
  selectedCrew: [],
  crewLeadId: null,
  sendCrewLeadNotifications: false,
  selectedWorkareaIds: [],
  repeatUnit: 'month',
  repeatCount: 1,
  repeatPlace: ['2nd'],
  repeatQuarter: [1],
  repeatMonth: [4],
  repeatEndType: 'date',
  repeatOccurrences: 1,
  repeatEndsOn: dayjs('2026-12-31'),
  endDateMode: 'sameDay',
  manhoursPerDay: 8,
  totalEstimatedHours: 0,
  workDays: [1, 2, 3, 4, 5],
  notes: '',
  frozen: false,
})

export const reconcileFormValues = (prev: EventFormData, updates: Partial<EventFormData>): EventFormData => {
  const next = { ...prev, ...updates }

  next.endDate = resolveEndDate(next)

  const recurrenceFieldChanged =
    next.repeatUnit !== prev.repeatUnit ||
    next.repeatCount !== prev.repeatCount ||
    next.startDate !== prev.startDate ||
    next.repeatPlace !== prev.repeatPlace ||
    next.repeatMonth !== prev.repeatMonth ||
    next.repeatEndType !== prev.repeatEndType

  if (next.repeatEndType === 'occurrences' && (recurrenceFieldChanged || next.repeatOccurrences !== prev.repeatOccurrences)) {
    const newEnd = calculateRepeatEndsOnFromOccurrences(next)
    if (newEnd) {
      next.repeatEndsOn = newEnd
    }
  }

  if (next.repeatEndType === 'date' && (recurrenceFieldChanged || next.repeatEndsOn !== prev.repeatEndsOn)) {
    const count = calculateOccurrencesFromEndDate(next)
    if (count !== null) {
      next.repeatOccurrences = count
    }
  }

  return next
}

export const buildAllDayChangeUpdates = (checked: boolean, prev: EventFormData, defaultDurationMinutes: number): Partial<EventFormData> => {
  const updates: Partial<EventFormData> = { isAllDay: checked }
  if (!checked) {
    updates.endDateMode = 'manual'
    if (prev.estimatedDuration.seconds === 0) {
      updates.estimatedDuration = Duration.minutes(defaultDurationMinutes)
    }
    if (prev.startTime.hour() === 0 && prev.startTime.minute() === 0) {
      updates.startTime = dayjs().startOf('day').add(8, 'hour')
    }
  }
  return updates
}

export const buildCrewSelectUpdates = (selectedIds: number[], allCrew: CrewMember[], currentLeadId: number | null): Partial<EventFormData> => {
  const selectedCrew = selectedIds
    .map((id) => allCrew.find((member) => member.id === id))
    .filter(Boolean) as CrewMember[]

  const updates: Partial<EventFormData> = { selectedCrew }

  if (!currentLeadId && selectedCrew.length > 0) {
    const firstWithEmail = selectedCrew.find((member) => member.userEmail)
    if (firstWithEmail) {
      updates.crewLeadId = firstWithEmail.id
    }
  }

  return updates
}

export const buildWorkareaScheduleUpdates = (
  configJson: string,
  prev: EventFormData,
  defaultDurationMinutes: number,
): { formUpdates: Partial<EventFormData>; formFieldValues: Record<string, any> } => {
  const parsed = parseWorkareaScheduleForEvent(configJson)

  const formUpdates: Partial<EventFormData> = {
    isAllDay: false,
    repeatUnit: parsed.repeatUnit,
    repeatCount: parsed.repeatCount,
    repeatPlace: parsed.repeatPlace,
    repeatEndType: parsed.repeatEndType,
    repeatOccurrences: parsed.repeatOccurrences,
  }

  if (prev.isAllDay) {
    if (prev.estimatedDuration.seconds === 0) {
      formUpdates.estimatedDuration = Duration.minutes(defaultDurationMinutes)
    }
    if (prev.startTime.hour() === 0 && prev.startTime.minute() === 0) {
      formUpdates.startTime = dayjs().startOf('day').add(8, 'hour')
    }
  }

  if (parsed.startDate) {
    formUpdates.startDate = parsed.startDate
  }
  if (parsed.repeatEndsOn) {
    formUpdates.repeatEndsOn = parsed.repeatEndsOn
  }

  const formFieldValues = {
    repeatUnit: parsed.repeatUnit,
    repeatCount: parsed.repeatCount,
    repeatPlace: parsed.repeatPlace,
    startDate: parsed.startDate ?? prev.startDate,
  }

  return { formUpdates, formFieldValues }
}

type RecurringValidationInput = {
  repeatUnit: string
  repeatPlace: string[]
  repeatMonth: number[]
}

export const areRecurringFieldsValid = (formData: RecurringValidationInput): boolean => {
  switch (formData.repeatUnit) {
    case 'none':
    case 'day':
    case 'week':
      return true
    case 'month':
      return !!(formData.repeatPlace && formData.repeatPlace.length > 0)
    case 'year':
      return !!(
        formData.repeatPlace &&
        formData.repeatPlace.length > 0 &&
        formData.repeatMonth &&
        formData.repeatMonth.length > 0
      )
    default:
      return true
  }
}
